import { useEffect, useRef, useState } from "react";

interface SectionHeadingProps {
  kicker: string;
  title: string;
  desc?: string;
  align?: "left" | "center";
}

export default function SectionHeading({ kicker, title, desc, align = "left" }: SectionHeadingProps) {
  const isCenter = align === "center";
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${isCenter ? "max-w-2xl mx-auto text-center" : "max-w-2xl"}`}
    >
      <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-700">
        <span className="w-6 h-px bg-primary-400" />
        {kicker}
      </span>
      <h2 className="mt-3 font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-snug">
        {title}
      </h2>
      {desc ? <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">{desc}</p> : null}
    </div>
  );
}